import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Switch, Route, useLocation } from 'react-router-dom';
import { Layout, Menu } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

import { PrivateComponent } from 'components/auth';
import { Link } from 'components/layout';
import { GET_MY_SCOPE_TYPES } from 'gql/auth';
import './style.less';
import Faculties from './faculties/Faculties';
import AddFaculty from './faculties/AddFaculty';
import EditFaculty from './faculties/EditFaculty';
import EditDepartment from './faculties/EditDepartment';
import Users from './users/Users';
import AddUser from './users/AddUser';
import EditUser from './users/EditUser';
import Roles from './roles/Roles';
import AddRole from './roles/AddRole';
import EditRole from './roles/EditRole';
import ProjectCategory from './projectCategories/ProjectCategories';
import AddProjectCategory from './projectCategories/AddProjectCategory';
import CopyProjectCategory from './projectCategories/CopyProjectCategory';
import EditProjectCategory from './projectCategories/EditProjectCategory';
import ApprovalRoutes from './approvalRoutes/ApprovalRoutes';
import AddApprovalRoute from './approvalRoutes/AddApprovalRoute';
import EditApprovalRoute from './approvalRoutes/EditApprovalRoute';
import { ProjectTypes, AddProjectType, EditProjectType } from './projectTypes';
import {
  FundingSources,
  AddFundingSource,
  EditFundingSource,
} from './fundingSources';
import {
  TeamMemberTypes,
  AddTeamMemberType,
  EditTeamMemberType,
} from './teamMemberTypes';
import {
  AddProjectStatus,
  ProjectStatus,
  EditProjectStatus,
} from './projectStatus';
import {
  AddEmailTemplate,
  EditEmailTemplate,
  EmailTemplates,
} from './emailTemplates';
import {
  AddBudgetCategory,
  BudgetCategories,
  EditBudgetCategory,
} from './budgetCategories';
import { EmailLogs, EmailLog } from './emailLogs';
import {
  ReportingPeriod,
  AddReportingPeriod,
  EditReportingPeriod,
} from './reportingPeriod';
import {
  ProjectApplicationSettings,
  AddProjectApplicationSetting,
  EditProjectApplicationSetting,
} from './projectApplicationSetting';
import { PageContainer } from '@ant-design/pro-layout';

const { Content, Sider } = Layout;

// Routes
const routes = [
  {
    key: 'users',
    path: '/system-setting/users',
    component: Users,
  },
  {
    key: 'addUser',
    path: '/system-setting/users/add',
    component: AddUser,
  },
  {
    key: 'editUser',
    path: '/system-setting/users/:id',
    component: EditUser,
  },
  {
    key: 'roles',
    path: '/system-setting/roles',
    component: Roles,
  },
  {
    key: 'addRole',
    path: '/system-setting/roles/add',
    component: AddRole,
  },
  {
    key: 'editRole',
    path: '/system-setting/roles/:id',
    component: EditRole,
  },
  {
    key: 'faculties',
    path: '/system-setting/faculties',
    component: Faculties,
  },
  {
    key: 'addFaculty',
    path: '/system-setting/faculties/add',
    component: AddFaculty,
  },
  {
    key: 'editFaculty',
    path: '/system-setting/faculties/:id',
    component: EditFaculty,
  },
  {
    key: 'editDepartment',
    path: '/system-setting/faculties/departments/:id',
    component: EditDepartment,
  },
  {
    key: 'projectCategory',
    path: '/system-setting/project-categories',
    component: ProjectCategory,
  },
  {
    key: 'addProjectCategory',
    path: '/system-setting/project-categories/add',
    component: AddProjectCategory,
  },
  {
    key: 'copyProjectCategory',
    path: '/system-setting/project-categories/:id/copy',
    component: CopyProjectCategory,
  },
  {
    key: 'editProjectCategory',
    path: [
      '/system-setting/project-categories/:id/basic',
      '/system-setting/project-categories/:id/role-hierarchy',
      '/system-setting/project-categories/:id/role-hierarchy/:type',
      '/system-setting/project-categories/:id/report-settings',
      '/system-setting/project-categories/:id/roles',
      '/system-setting/project-categories/:id/roles/add',
      '/system-setting/project-categories/:id/roles/:overriddenScopeId',
      '/system-setting/project-categories/:id/approval-routes',
    ],
    component: EditProjectCategory,
  },
  {
    key: 'approvalRoutes',
    path: '/system-setting/approval-routes',
    component: ApprovalRoutes,
  },
  {
    key: 'addApprovalRoute',
    path: '/system-setting/approval-routes/add',
    component: AddApprovalRoute,
  },
  {
    key: 'editApprovalRoute',
    path: '/system-setting/approval-routes/:id',
    component: EditApprovalRoute,
  },
  {
    key: 'projectTypes',
    path: '/system-setting/project-types',
    component: ProjectTypes,
  },
  {
    key: 'addProjectType',
    path: '/system-setting/project-types/add',
    component: AddProjectType,
  },
  {
    key: 'editProjectType',
    path: '/system-setting/project-types/:id',
    component: EditProjectType,
  },
  {
    key: 'fundingSources',
    path: '/system-setting/funding-sources',
    component: FundingSources,
  },
  {
    key: 'addFundingSource',
    path: '/system-setting/funding-sources/add',
    component: AddFundingSource,
  },
  {
    key: 'editFundingSource',
    path: '/system-setting/funding-sources/:id',
    component: EditFundingSource,
  },
  {
    key: 'teamMemberTypes',
    path: '/system-setting/team-member-types',
    component: TeamMemberTypes,
  },
  {
    key: 'addTeamMemberTypes',
    path: '/system-setting/team-member-types/add',
    component: AddTeamMemberType,
  },
  {
    key: 'editTeamMemberType',
    path: '/system-setting/team-member-types/:id',
    component: EditTeamMemberType,
  },
  {
    key: 'projectStatus',
    path: '/system-setting/project-status',
    component: ProjectStatus,
  },
  {
    key: 'addProjectStatus',
    path: '/system-setting/project-status/add',
    component: AddProjectStatus,
  },
  {
    key: 'editProjectStatus',
    path: '/system-setting/project-status/:id',
    component: EditProjectStatus,
  },
  {
    key: 'emailTemplates',
    path: '/system-setting/email-templates',
    component: EmailTemplates,
  },
  {
    key: 'addEmailTemplate',
    path: '/system-setting/email-templates/add',
    component: AddEmailTemplate,
  },
  {
    key: 'editEmailTemplate',
    path: '/system-setting/email-templates/:id',
    component: EditEmailTemplate,
  },
  {
    key: 'budgetCategories',
    path: '/system-setting/budget-categories',
    component: BudgetCategories,
  },
  {
    key: 'addEmailTemplate',
    path: '/system-setting/budget-categories/add',
    component: AddBudgetCategory,
  },
  {
    key: 'editEmailTemplate',
    path: '/system-setting/budget-categories/:id',
    component: EditBudgetCategory,
  },
  {
    key: 'emailLogs',
    path: '/system-setting/email-logs',
    component: EmailLogs,
  },
  {
    key: 'emailLog',
    path: '/system-setting/email-logs/:id',
    component: EmailLog,
  },
  {
    key: 'reportingPeriod',
    path: '/system-setting/reporting-period',
    component: ReportingPeriod,
  },
  {
    key: 'addReportingPeriod',
    path: '/system-setting/reporting-period/add',
    component: AddReportingPeriod,
  },
  {
    key: 'editReportingPeriod',
    path: '/system-setting/reporting-period/:id',
    component: EditReportingPeriod,
  },
  {
    key: 'addProjectApplicationSetting',
    path: '/system-setting/project-application-settings/add',
    component: AddProjectApplicationSetting,
  },
  {
    key: 'editProjectApplicationSetting',
    path: '/system-setting/project-application-settings/:id',
    component: EditProjectApplicationSetting,
  },
  {
    key: 'projectApplicationSettings',
    path: '/system-setting/project-application-settings',
    component: ProjectApplicationSettings,
  },
];

// For mapping the sider menu
const siderLinks = [
  {
    key: 'dashboard',
    to: '/system-setting/dashboard',
    text: 'Dashboard',
  },
  {
    key: 'reporting-period',
    to: '/system-setting/reporting-period',
    text: 'Annual Reporting Period',
  },
  {
    key: 'approval-routes',
    to: '/system-setting/approval-routes',
    text: 'Approval Route Templates',
  },
  {
    key: 'budget-categories',
    to: '/system-setting/budget-categories',
    text: 'Budget Categories',
  },
  {
    key: 'email-logs',
    to: '/system-setting/email-logs',
    text: 'Email Logs',
    expectedScope: 'WRITE_EMAIL_TEMPLATE',
  },
  {
    key: 'email-templates',
    to: '/system-setting/email-templates',
    text: 'Email Templates',
    expectedScope: 'WRITE_EMAIL_TEMPLATE',
  },
  {
    key: 'faculties',
    to: '/system-setting/faculties',
    text: 'Faculties & Departments',
  },
  {
    key: 'funding-sources',
    to: '/system-setting/funding-sources',
    text: 'Funding Sources',
  },
  {
    key: 'project-application-settings',
    to: '/system-setting/project-application-settings',
    text: 'Funding Application Settings',
    expectedScope: 'WRITE_PROJECT_APPLICATION_SETTING',
  },
  {
    key: 'project-categories',
    to: '/system-setting/project-categories',
    text: 'Project Categories',
  },
  {
    key: 'project-status',
    to: '/system-setting/project-status',
    text: 'Project Status',
  },
  {
    key: 'project-types',
    to: '/system-setting/project-types',
    text: 'Project Types',
  },
  {
    key: 'roles',
    to: '/system-setting/roles',
    text: 'Roles',
  },
  {
    key: 'team-member-types',
    to: '/system-setting/team-member-types',
    text: 'Team Member Types',
  },
  {
    key: 'users',
    to: '/system-setting/users',
    text: 'Users',
  },
];

const SystemSetting = () => {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { data, loading } = useQuery(GET_MY_SCOPE_TYPES, {
    // fetchPolicy: 'cache-and-network',
  });
  const toggle = () => {
    setCollapsed((previous) => !previous);
  };
  const myScopeTypes = data?.myScopeTypes;

  return (
    <PageContainer loading={loading} ghost className="setting-page-container">
      <Layout
        style={{ padding: '24px 0', background: '#fff', alignItems: 'stretch' }}
      >
        <Sider
          width={206}
          collapsible
          trigger={null}
          collapsed={collapsed}
          style={{ background: '#fff', borderRight: '1px solid #f0f0f0' }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '8px 16px 8px 8px',
              background: '#fff',
            }}
          >
            {collapsed ? (
              <RightOutlined onClick={toggle} style={{ fontSize: 20 }} />
            ) : (
              <LeftOutlined onClick={toggle} style={{ fontSize: 20 }} />
            )}
          </div>
          <Menu
            mode="inline"
            selectedKeys={[pathname.split('/')[2]]}
            style={{ borderRight: 'none' }}
          >
            {siderLinks.map(({ key, to, text, expectedScope }) =>
              expectedScope ? (
                myScopeTypes?.includes(expectedScope) ? (
                  <Menu.Item key={key} style={{ paddingLeft: '24px' }}>
                    <PrivateComponent
                      key={key}
                      expectedScopeType={[expectedScope]}
                    >
                      <Link to={to}>{text}</Link>
                    </PrivateComponent>
                  </Menu.Item>
                ) : null
              ) : (
                <Menu.Item key={key}>
                  <Link to={to}>{text}</Link>
                </Menu.Item>
              )
            )}
          </Menu>
        </Sider>
        <Content
          style={{ padding: '8px 40px' }}
          className="system-setting-content"
        >
          <Switch>
            <Route exact path="/system-setting/dashboard">
              {<p>dashboard</p>}
            </Route>
            {routes.map(({ path, component, key }) => (
              <Route exact path={path} component={component} key={key} />
            ))}
          </Switch>
        </Content>
      </Layout>
    </PageContainer>
  );
};

export default SystemSetting;
